import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';
import { LessonContentSchema } from '../lesson-content/lesson-content';

export const LESSON_TITLE_MAX_LENGTH = 160;

/** Per-locale lesson copy (N5, W7) — the title AND the body. */
export const LessonTranslationSchema = z
  .object({
    title: z.string().trim().min(1).max(LESSON_TITLE_MAX_LENGTH),
    content: LessonContentSchema,
  })
  .strict();

export type LessonTranslation = z.infer<typeof LessonTranslationSchema>;

export const LessonTranslationsSchema = makeTranslationsSchema(LessonTranslationSchema);

/**
 * One lesson of a course — the unit a member completes (matrix rows 1–2).
 *
 * The list shape: title and length in the caller's locale, without the body. A
 * course with forty lessons would otherwise ship forty documents to render a
 * table of contents, in both locales, on every open.
 */
export const LessonSchema = z
  .object({
    id: UuidSchema,
    courseId: UuidSchema,
    sortOrder: z.number().int().nonnegative(),
    title: z.string(),
    /** Readable characters in the caller's locale — what a reading estimate uses. */
    contentLength: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type Lesson = z.infer<typeof LessonSchema>;

/**
 * The member's table of contents: one row per lesson, with the read mark.
 *
 * The body is deliberately absent — that is `LessonContentViewSchema`'s job, and
 * `LessonSchema`'s docblock above says why a forty-lesson course must not ship
 * forty documents to draw a list.
 *
 * It exists because `CourseProgress.nextLessonId` is the *first unread* lesson,
 * so it goes `null` the moment a course is finished: without this shape a
 * completed course has no reachable lesson at all, and a member can neither
 * re-read one nor jump to a lesson out of order.
 */
export const MemberLessonSchema = LessonSchema.extend({
  isCompleted: z.boolean(),
}).strict();

export type MemberLesson = z.infer<typeof MemberLessonSchema>;

/** The reader's payload: the body, in the caller's locale only. */
export const LessonContentViewSchema = LessonSchema.extend({
  content: LessonContentSchema,
  /** Whether this member has already marked it read (matrix row 2 — resumable). */
  isCompleted: z.boolean(),
}).strict();

export type LessonContentView = z.infer<typeof LessonContentViewSchema>;

/** What the admin edit form reads: every locale, not just the caller's (W7). */
export const LessonDetailSchema = LessonSchema.extend({
  translations: LessonTranslationsSchema,
}).strict();

export type LessonDetail = z.infer<typeof LessonDetailSchema>;
